import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Heart, Calendar, MapPin, Clock, User, ChevronLeft,
  Bell, BellOff, Trash2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import WhatsAppShareButton from '../components/WhatsAppShareButton';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, lang } = useTranslation();
  
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    }
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error(t('favorites.toastLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/favorites/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setFavorites(prev => prev.filter(f => f.session_id !== sessionId));
        toast.success(t('favorites.toastRemoved'));
      }
    } catch (error) {
      toast.error(t('favorites.toastRemoveError'));
    }
  };

  const toggleNotification = async (sessionId, currentValue) => {
    try {
      const response = await fetch(`${API_URL}/api/favorites/${sessionId}/notify`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(prev => prev.map(f => 
          f.session_id === sessionId 
            ? { ...f, notify_on_availability: data.notify_on_availability }
            : f
        ));
        toast.success(data.message);
      }
    } catch (error) {
      toast.error(t('favorites.toastUpdateError'));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const availableFavorites = favorites.filter(f => f.session && !f.session.is_full);
  const fullFavorites = favorites.filter(f => f.session && f.session.is_full);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2" data-testid="favorites-login-title">{t('favorites.loginRequired')}</h2>
          <p className="text-muted-foreground mb-4">{t('favorites.loginToSee')}</p>
          <Link to="/login">
            <Button className="btn-primary">{t('favorites.login')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)} 
              className="text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              <span className="font-bold uppercase" data-testid="favorites-page-title">{t('favorites.title')}</span>
            </div>
            <div className="w-6"></div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-3 bg-card border border-border rounded-full px-6 py-3">
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
            <span className="text-lg">
              <span className="font-bold text-primary">{favorites.length}</span>{' '}
              {favorites.length > 1 ? t('favorites.sessionsSaved') : t('favorites.sessionSaved')}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : favorites.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">{t('favorites.noFavorites')}</h3>
            <p className="text-muted-foreground mb-6">{t('favorites.noFavoritesDesc')}</p>
            <Link to="/explore">
              <Button className="btn-primary">{t('favorites.exploreSessions')}</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {availableFavorites.length > 0 && (
              <div>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  {t('favorites.available')} ({availableFavorites.length})
                </h2>
                <div className="space-y-4">
                  {availableFavorites.map((favorite) => (
                    <FavoriteCard
                      key={favorite.favorite_id}
                      favorite={favorite}
                      onRemove={removeFavorite}
                      onToggleNotification={toggleNotification}
                      formatDate={formatDate}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            )}

            {fullFavorites.length > 0 && (
              <div>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  {t('favorites.fullWaiting')} ({fullFavorites.length})
                </h2>
                <p className="text-sm text-muted-foreground mb-4">{t('favorites.notifyWhenAvailable')}</p>
                <div className="space-y-4">
                  {fullFavorites.map((favorite) => (
                    <FavoriteCard
                      key={favorite.favorite_id}
                      favorite={favorite}
                      onRemove={removeFavorite}
                      onToggleNotification={toggleNotification}
                      formatDate={formatDate}
                      isFull={true}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FavoriteCard({ favorite, onRemove, onToggleNotification, formatDate, isFull = false, t }) {
  const session = favorite.session;
  if (!session) return null;

  return (
    <div 
      className={`bg-card border border-border rounded-xl overflow-hidden transition-all ${
        isFull ? 'opacity-75' : 'card-hover'
      }`}
      data-testid={`favorite-card-${favorite.session_id}`}
    >
      <div className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                isFull 
                  ? 'bg-red-500/20 text-red-400' 
                  : 'bg-green-500/20 text-green-400'
              }`}>
                {isFull ? t('favorites.full') : `${session.spots_left} ${session.spots_left > 1 ? t('favorites.spots') : t('favorites.spot')}`}
              </span>
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                {session.category}
              </span>
            </div>
            
            <Link to={`/session/${session.session_id}`}>
              <h3 className="font-bold text-lg hover:text-primary transition-colors">
                {session.title}
              </h3>
            </Link>
            
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(session.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {session.start_time}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {session.location}
              </span>
            </div>
            
            <div className="mt-3 text-lg font-bold text-primary">
              {session.price}&euro;
            </div>
          </div>

          <div className="flex sm:flex-col items-center gap-2">
            <button
              onClick={() => onToggleNotification(favorite.session_id, favorite.notify_on_availability)}
              className={`p-2 rounded-lg transition-colors ${
                favorite.notify_on_availability
                  ? 'bg-primary/20 text-primary'
                  : 'bg-muted text-muted-foreground'
              }`}
              title={favorite.notify_on_availability ? t('favorites.notifEnabled') : t('favorites.notifDisabled')}
              data-testid={`toggle-notify-${favorite.session_id}`}
            >
              {favorite.notify_on_availability ? (
                <Bell className="w-5 h-5" />
              ) : (
                <BellOff className="w-5 h-5" />
              )}
            </button>

            {!isFull && (
              <WhatsAppShareButton session={session} variant="icon" />
            )}

            <Link to={`/session/${session.session_id}`}>
              <button className="p-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <ExternalLink className="w-5 h-5" />
              </button>
            </Link>

            <button
              onClick={() => onRemove(favorite.session_id)}
              className="p-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
              title={t('favorites.removeFromFavorites')}
              data-testid={`remove-favorite-${favorite.session_id}`}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {!isFull && (
        <div className="px-5 py-3 bg-primary/10 border-t border-border">
          <Link to={`/session/${session.session_id}`}>
            <Button className="btn-primary w-full sm:w-auto">{t('favorites.bookNow')}</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
